import {
  createEntityAdapter,
  createSelector,
  createSlice,
  EntityState,
} from "@reduxjs/toolkit";

import {
  IdentityVerification,
  ParentEntityType,
  ProfileVerification,
} from "../../../api/verificationsV2";
import { RootState } from "../../../app/store";
import { RequestStatus } from "../../legalTechnicalAnalysis/Logic/assetsSlice";
import {
  fetchIdentityVerifications,
  fetchProfileVerifications,
  upsertIdentityVerifications,
  upsertProfileVerifications,
} from "./verificationsThunk";

export const identityVerificationsAdapter = createEntityAdapter<
  IdentityVerification,
  string
>({
  selectId: (v) => v.verification_id,
});

export const profileVerificationsAdapter = createEntityAdapter<
  ProfileVerification,
  string
>({
  selectId: (v) => v.verification_id,
});

export interface VerificationsState {
  identityVerifications: EntityState<IdentityVerification, string>;
  identityStatus: RequestStatus;
  identityError?: string | null;
  identityCaseId?: string;

  profileVerifications: EntityState<ProfileVerification, string>;
  profileStatus: RequestStatus;
  profileError?: string | null;
  profileCaseId?: string;
}

const initialState: VerificationsState = {
  identityVerifications: identityVerificationsAdapter.getInitialState(),
  identityStatus: "idle",
  identityError: null,
  identityCaseId: undefined,

  profileVerifications: profileVerificationsAdapter.getInitialState(),
  profileStatus: "idle",
  profileError: null,
  profileCaseId: undefined,
};

const verificationsSlice = createSlice({
  name: "verifications",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchIdentityVerifications.pending, (state) => {
        state.identityStatus = "loading";
        state.identityError = null;
      })
      .addCase(fetchIdentityVerifications.fulfilled, (state, action) => {
        state.identityStatus = "success";
        state.identityCaseId = action.meta.arg;

        identityVerificationsAdapter.setAll(
          state.identityVerifications,
          action.payload.verifications,
        );
      })
      .addCase(fetchIdentityVerifications.rejected, (state, action) => {
        state.identityStatus = "failed";
        state.identityError =
          action.payload ?? "Failed to fetch identity verifications";
      })

      .addCase(fetchProfileVerifications.pending, (state) => {
        state.profileStatus = "loading";
        state.profileError = null;
      })
      .addCase(fetchProfileVerifications.fulfilled, (state, action) => {
        state.profileStatus = "success";
        state.profileCaseId = action.meta.arg;

        profileVerificationsAdapter.setAll(
          state.profileVerifications,
          action.payload.verifications,
        );
      })
      .addCase(fetchProfileVerifications.rejected, (state, action) => {
        state.profileStatus = "failed";
        state.profileError =
          action.payload ?? "Failed to fetch profile verifications";
      })

      .addCase(upsertIdentityVerifications.fulfilled, (state, action) => {
        identityVerificationsAdapter.upsertMany(
          state.identityVerifications,
          action.meta.arg.verifications,
        );
      })
      .addCase(upsertProfileVerifications.fulfilled, (state, action) => {
        profileVerificationsAdapter.upsertMany(
          state.profileVerifications,
          action.meta.arg.verifications,
        );
      });
  },
});

export const verificationsReducer = verificationsSlice.reducer;

const identitySelectors = identityVerificationsAdapter.getSelectors<RootState>(
  (state) => state.verifications.identityVerifications,
);

const profileSelectors = profileVerificationsAdapter.getSelectors<RootState>(
  (state) => state.verifications.profileVerifications,
);

export const selectIdentityVerifications = createSelector(
  [
    identitySelectors.selectAll,
    (state: RootState, parentEntityType: ParentEntityType) => parentEntityType,
    (
      state: RootState,
      parentEntityType: ParentEntityType,
      parentEntityId: string,
    ) => parentEntityId,
    (
      state: RootState,
      parentEntityType: ParentEntityType,
      parentEntityId: string,
      stakeholderId: string,
    ) => stakeholderId,
  ],
  (verifications, parentEntityType, parentEntityId, stakeholderId) =>
    verifications.filter(
      (verification) =>
        verification.parent_entity_type === parentEntityType &&
        verification.parent_entity_id === parentEntityId &&
        verification.stakeholder_id === stakeholderId,
    ),
);

export const selectProfileVerifications = createSelector(
  [
    profileSelectors.selectAll,
    (state: RootState, parentEntityType: ParentEntityType) => parentEntityType,
    (
      state: RootState,
      parentEntityType: ParentEntityType,
      parentEntityId: string,
    ) => parentEntityId,
    (
      state: RootState,
      parentEntityType: ParentEntityType,
      parentEntityId: string,
      stakeholderId: string,
    ) => stakeholderId,
  ],
  (verifications, parentEntityType, parentEntityId, stakeholderId) =>
    verifications.filter(
      (verification) =>
        verification.parent_entity_type === parentEntityType &&
        verification.parent_entity_id === parentEntityId &&
        verification.stakeholder_id === stakeholderId,
    ),
);

export const selectIdentityVerificationsStatus = (state: RootState) =>
  state.verifications.identityStatus;

export const selectIdentityVerificationsError = (state: RootState) =>
  state.verifications.identityError;

export const selectProfileVerificationsStatus = (state: RootState) =>
  state.verifications.profileStatus;

export const selectProfileVerificationsError = (state: RootState) =>
  state.verifications.profileError;
